import {Getters} from "vuex-smart-module";

import dayjs from "dayjs";

import UtilityCounterState, {UtilityCounterStatus} from "@/store/modules/utility-counter/utility-counter-state";

import {UtilityCounterData} from "@/http/data/utility-counter-data";

import UtilityCounterReadingData from "@/http/data/utility-counter-reading-data";

export interface UtilityCounterCompletion {
    completed: number;
    total: number;
}

function resolveStatusMessage(status: UtilityCounterStatus, subject: "counters"|"readings"): string {
    switch (status) {
        case UtilityCounterStatus.Unauthenticated: return "Authentication required.";

        case UtilityCounterStatus.Forbidden: return "Insufficient privileges.";

        case UtilityCounterStatus.Empty:
            return subject === "counters"
                ? "No active utility counters found."
                : "No counter readings found.";

        case UtilityCounterStatus.Failed:
            return subject === "counters"
                ? "Failed to load utility counters."
                : "Failed to load counter readings.";

        case UtilityCounterStatus.Loading:
            return subject === "counters"
                ? "Loading utility counters."
                : "Loading counter readings.";

        case UtilityCounterStatus.Ready: return "";

        default: return "Status unknown.";
    }
}

function isReadingNewer(candidate: UtilityCounterReadingData, current: UtilityCounterReadingData): boolean {
    const candidateDate = dayjs(candidate.date);
    const currentDate = dayjs(current.date);

    if (!candidateDate.isSame(currentDate, "day")) {
        return candidateDate.isAfter(currentDate, "day");
    }

    const candidateTimestamp = candidate.inserted_at
        ? dayjs(candidate.inserted_at).valueOf()
        : 0;

    const currentTimestamp = current.inserted_at
        ? dayjs(current.inserted_at).valueOf()
        : 0;

    if (candidateTimestamp !== currentTimestamp) {
        return candidateTimestamp > currentTimestamp;
    }

    return candidate.id > current.id;
}

export default class UtilityCounterGetters extends Getters<UtilityCounterState> {

    get activeCounters(): UtilityCounterData[] {
        return this.state.counters.filter(counter => counter.active);
    }

    get latestReadingByCounterId(): Record<number, UtilityCounterReadingData> {
        const readingsByCounter: Record<number, UtilityCounterReadingData> = {};
        const fromDate = this.state.readingsFromDate;
        const untilDate = this.state.readingsUntilDate;

        for (const reading of this.state.readings) {
            if (!reading.is_latest) continue;
            if (!reading.date || !dayjs(reading.date).isValid()) continue;

            const readingDate = dayjs(reading.date);

            if (fromDate && readingDate.isBefore(fromDate, "day")) continue;
            if (untilDate && readingDate.isAfter(untilDate, "day")) continue;

            const current = readingsByCounter[reading.counter_id];

            if (!current || isReadingNewer(reading, current)) {
                readingsByCounter[reading.counter_id] = reading;
            }
        }

        return readingsByCounter;
    }

    get completion(): UtilityCounterCompletion {
        const latestReadings = this.getters.latestReadingByCounterId;

        const total = this.getters.activeCounters.length;

        const completed = this.getters.activeCounters.filter(
            counter => Boolean(latestReadings[counter.id])
        ).length;

        return {completed, total};
    }

    get allActiveCountersHaveReading(): boolean {
        const completion = this.getters.completion;

        return (completion.total > 0 && completion.completed === completion.total);
    }

    get pendingCounters(): UtilityCounterData[] {
        const latestReadings = this.getters.latestReadingByCounterId;

        return this.getters.activeCounters.filter(counter => !latestReadings[counter.id]);
    }

    get countersStatusMessage(): string {
        return resolveStatusMessage(this.state.countersStatus, "counters");
    }

    get readingsStatusMessage(): string {
        return resolveStatusMessage(this.state.readingsStatus, "readings");
    }

    get previousReadingByCounterId(): Record<number, UtilityCounterReadingData> {
        const readingsByCounter: Record<number, UtilityCounterReadingData> = {};
        const fromDate = this.state.readingsFromDate;

        if (!fromDate) return readingsByCounter;

        for (const reading of this.state.readings) {
            if (!reading.is_latest) continue;
            if (!reading.date || !dayjs(reading.date).isValid()) continue;

            const readingDate = dayjs(reading.date);

            if (!readingDate.isBefore(fromDate, "day")) continue;

            const current = readingsByCounter[reading.counter_id];

            if (!current || isReadingNewer(reading, current)) {
                readingsByCounter[reading.counter_id] = reading;
            }
        }

        return readingsByCounter;
    }

}